import { Button } from '@cibli/shared';
import { ArrowDown, ArrowLeft, Loader2, Trash } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';

import { useDashboardUserSync } from '@/features/common/hooks/useDashboardUserSync';
import CVDisplayScreen from '@/features/cv/pages/CVDisplayScreen';

import ConfirmDeleteCVDialog from '../components/ConfirmDeleteCVDialog';
import { useCVEdit } from '../hooks/useCVEdit';
import { useDeleteCV } from '../hooks/useDeleteCV';
import { useDownloadCV } from '../hooks/useDownloadCV';

export default function CVEditPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();

  useDashboardUserSync();

  const { cv, setCv, isLoading, isSaving, error, handleSave } = useCVEdit(id);

  const { downloadCVFromList, isDownloading, downloadingId } = useDownloadCV();
  const { deleteCVById, deletingId } = useDeleteCV();
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const isDeletingCurrent = id ? deletingId === id : false;
  const isDownloadingCurrent = id ? downloadingId === id && isDownloading : false;

  const handleDeleteClick = () => {
    if (id) {
      setConfirmDeleteId(id);
    }
  };

  const handleDownload = () => {
    if (!id || !cv) return;
    void downloadCVFromList({
      id,
      firstName: cv.firstName ?? '',
      lastName: cv.lastName ?? '',
    });
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex flex-col items-center justify-center h-full gap-4 text-muted-foreground">
          <Loader2 className="size-8 animate-spin" />
          <span>{t('dashboard.cvList.loading')}</span>
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex flex-col items-center justify-center h-full gap-4 text-destructive">
          <p>{error}</p>
          <Button variant="outline" onClick={() => navigate('/dashboard')}>
            {t('dashboard.cvEdit.back')}
          </Button>
        </div>
      );
    }

    if (!cv) return null;

    return (
      <CVDisplayScreen
        cv={cv}
        onEdit={setCv}
        onGenerateCV={handleSave}
        isGeneratingCV={isSaving}
        formConfirmationText={t('dashboard.cvEdit.validate')}
        backButton={
          <Button
            variant="secondary"
            className="rounded-full shadow-lg"
            onClick={() => navigate('/dashboard')}
            aria-label={t('dashboard.cvEdit.back')}
            title={t('dashboard.cvEdit.back')}
          >
            <ArrowLeft className="size-5" />
            {t('dashboard.cvEdit.back')}
          </Button>
        }
        headerActions={
          <>
            <Button
              variant="destructive"
              className="rounded-full shadow-lg"
              aria-label={t('dashboard.cvEdit.delete')}
              title={t('dashboard.cvEdit.delete')}
              onClick={handleDeleteClick}
              disabled={isDeletingCurrent}
            >
              {isDeletingCurrent ? (
                <Loader2 className="size-5 animate-spin" />
              ) : (
                <Trash className="size-5" />
              )}
              {t('dashboard.cvEdit.delete')}
            </Button>

            <Button
              variant="outline"
              className="rounded-full shadow-lg"
              onClick={handleDownload}
              disabled={!cv || isDownloadingCurrent}
            >
              {isDownloadingCurrent ? (
                <Loader2 className="size-5 animate-spin" />
              ) : (
                <ArrowDown className="size-5" />
              )}
              {t('dashboard.cvEdit.download')}
            </Button>
          </>
        }
      />
    );
  };

  return (
    <>
      {renderContent()}
      {id && (
        <ConfirmDeleteCVDialog
          cvId={confirmDeleteId}
          onClose={() => setConfirmDeleteId(null)}
          onConfirm={cvId => {
            setConfirmDeleteId(null);
            return deleteCVById(cvId, () => {
              navigate('/dashboard');
            });
          }}
          isDeleting={isDeletingCurrent}
        />
      )}
    </>
  );
}
